import { useMemo, useState } from 'react';
import { ArrowUpDown, ExternalLink, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { MapleMarketApp } from '@/data/mapleMarketApps';
import type { MarketplaceMemberRole } from '@/hooks/supabase/useMarketplaceMemberships';

interface AppsPanelProps {
  title?: string;
  description?: string;
  products: MapleMarketApp[];
  loading: boolean;
  mode?: 'manage' | 'team';
  isSiteAdmin: boolean;
  getRoleForProduct: (productId: string) => MarketplaceMemberRole | null;
  canManageProduct: (productId: string) => boolean;
  onOpenProduct: (productId: string, initialTab?: 'basic' | 'team' | 'publish' | 'keys') => void;
}

type SortField = 'name' | 'rating' | 'reviews';

function getRoleLabel(role: MarketplaceMemberRole | null, isSiteAdmin: boolean) {
  if (isSiteAdmin) {
    return 'site admin';
  }

  return role ?? 'viewer';
}

export default function AppsPanel({
  title = 'Applications',
  description = 'Manage listings, assets, pricing, and release settings for your visible products.',
  products,
  loading,
  mode = 'manage',
  isSiteAdmin,
  getRoleForProduct,
  canManageProduct,
  onOpenProduct,
}: AppsPanelProps) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const categories = useMemo(
    () => ['all', ...Array.from(new Set(products.map((product) => product.category)))],
    [products]
  );

  const filteredProducts = useMemo(() => {
    return [...products]
      .filter((product) => {
        const matchesCategory = category === 'all' || product.category === category;
        const query = search.trim().toLowerCase();
        const matchesSearch =
          query.length === 0 ||
          product.name.toLowerCase().includes(query) ||
          product.sku.toLowerCase().includes(query) ||
          product.developer.toLowerCase().includes(query);

        return matchesCategory && matchesSearch;
      })
      .sort((left, right) => {
        const multiplier = sortDirection === 'asc' ? 1 : -1;

        if (sortField === 'name') {
          return left.name.localeCompare(right.name) * multiplier;
        }

        if (sortField === 'rating') {
          return (left.rating - right.rating) * multiplier;
        }

        return (left.reviews - right.reviews) * multiplier;
      });
  }, [category, products, search, sortDirection, sortField]);

  const toggleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(field);
    setSortDirection('asc');
  };

  return (
    <div className="space-y-6 p-6 lg:p-8">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filter products</CardTitle>
          <CardDescription>Search by title, SKU, or developer and scope the list by category.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 lg:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search applications"
              className="pl-9"
            />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-full lg:w-[220px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((categoryOption) => (
                <SelectItem key={categoryOption} value={categoryOption}>
                  {categoryOption === 'all' ? 'All categories' : categoryOption}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{mode === 'team' ? 'Products with team access' : 'Product list'}</CardTitle>
          <CardDescription>
            {mode === 'team'
              ? 'Open a product to manage owners, admins, developers, and moderators.'
              : 'Open any accessible product to edit its marketplace listing, publication workflow, or Maple Keys.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-xl border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <button type="button" className="inline-flex items-center gap-1" onClick={() => toggleSort('name')}>
                      Application
                      <ArrowUpDown className="h-3.5 w-3.5" />
                    </button>
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>
                    <button type="button" className="inline-flex items-center gap-1" onClick={() => toggleSort('rating')}>
                      Rating
                      <ArrowUpDown className="h-3.5 w-3.5" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" className="inline-flex items-center gap-1" onClick={() => toggleSort('reviews')}>
                      Reviews
                      <ArrowUpDown className="h-3.5 w-3.5" />
                    </button>
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((product) => {
                  const role = getRoleForProduct(product.id);
                  const editable = canManageProduct(product.id);

                  return (
                    <TableRow key={product.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <img src={product.image} alt={product.name} className="h-11 w-11 rounded-lg object-cover" />
                          <div className="min-w-0">
                            <div className="truncate font-medium text-foreground">{product.name}</div>
                            <div className="truncate text-xs text-muted-foreground">{product.sku}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant={product.visibilityStatus === 'published' ? 'default' : 'secondary'} className="capitalize">
                            {product.visibilityStatus}
                          </Badge>
                          <Badge variant="outline" className="capitalize">
                            {product.publishRequestStatus}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={editable ? 'default' : 'secondary'} className="capitalize">
                          {getRoleLabel(role, isSiteAdmin)}
                        </Badge>
                      </TableCell>
                      <TableCell>{product.rating.toFixed(1)}</TableCell>
                      <TableCell>{product.reviews.toLocaleString()}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            type="button"
                            variant={editable ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => onOpenProduct(product.id, mode === 'team' ? 'team' : 'basic')}
                          >
                            {mode === 'team' ? 'Team' : editable ? 'Manage' : 'View'}
                          </Button>
                          <Button type="button" variant="ghost" size="icon" onClick={() => onOpenProduct(product.id, 'publish')}>
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredProducts.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center text-sm text-muted-foreground">
                      No products matched your current filters.
                    </TableCell>
                  </TableRow>
                )}
                {loading && (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center text-sm text-muted-foreground">
                      Loading marketplace products…
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
